import * as React from 'react';
import * as ReactDOM from 'react-dom';

import Router from 'src/ui/Router';
import registerServiceWorker from 'src/registerServiceWorker';
import 'src/ui/style.css';

import { NormalRand, RefMap, RefSet } from './util';
import App from 'src/state/App';
import checkBs58 from 'src/util/checkBs58';
import Room from 'src/state/Room';
import RoomPage from 'src/state/RoomPage';
import storage from 'src/storage';
import Player from 'src/state/Player';
import Calculator from 'src/state/Calculator';
import { Simulator, handleFrame as simHandleFrame } from 'src/state/Simulator';
import Game from './state/Game';

const qrcode = require('qrcode');

function isPreferredRoom(newData: Room, data: Room): boolean {
  const newGameCount = RefSet.Refs<Game>(newData.entities.games).length;
  const gameCount = RefSet.Refs<Game>(data.entities.games).length;

  return newGameCount >= gameCount;
}

let initRoomId: string | null = null;

const id = location.hash.split('#/r/')[1];

if (checkBs58(id)) {
  initRoomId = id;
}

let roomHints: RoomHints = (() => {
  const localStr = localStorage.getItem('bb:roomHints');

  if (localStr === null) {
    return {};
  }

  return JSON.parse(localStr) as RoomHints;
})();

const store = App.Store(initRoomId, roomHints);
(window as any).store = store; // tslint:disable-line

let frameLoopRunning = false;
let lastTime = Date.now();

const render = () => {
  ReactDOM.render(
    <Router store={store}/>,
    document.getElementById('root') as HTMLElement
  );

  const app = store.getState();
  renderHash(app);
  updateSync(app);
  updateRoomHintsSync(app);

  Calculator.onChange(app, store.dispatch);

  (() => {
    const page = App.CurrentPage(app);

    if (!page) {
      return;
    }

    const sim = page.simulator;

    if (!sim) {
      return;
    }

    simHandleFrame(sim, store);
  })();

  if (!frameLoopRunning) {
    frameLoopRunning = true;
    lastTime = Date.now();
    requestAnimationFrame(frameLoop);
  }
};

function renderHash(app: App) {
  const hash = Hash(app);

  if (location.hash !== hash) {
    location.hash = hash;

    const cvs = document.querySelector('#qr-canvas');

    if (cvs) {
      qrcode.toCanvas(cvs, location.href, logIfError);
    }
  }
}

function Hash(app: App) {
  return '#' + App.Location.toString(app.location);
}

window.addEventListener('hashchange', () => {
  const app = store.getState();

  const appHash = Hash(app);

  if (location.hash === appHash) {
    return;
  }

  const newLocation = App.Location(app, location.hash.slice(1));

  if (newLocation === null || !checkBs58(newLocation.data.id)) {
    renderHash(app);
    return;
  }

  const room = RefSet.resolve(app.entities.rooms, newLocation.data) || Room({ id: newLocation.data.id });

  store.dispatch({
    type: 'SetRoom',
    data: room,
  });
});

document.addEventListener('keydown', e => {
  // TODO: Need a better way of doing this type of thing.
  // TODO: Use arrow keys for moving selection in dialogs too.

  const app = store.getState();
  const page = App.CurrentPage(app);

  if (page === null) {
    return;
  }

  if (e.key === 'Escape' && !page.dialog.hidden) {
    RoomPage.hideDialog(store.dispatchCurrentRoomPage);
    return;
  }

  if (page.dialog.hidden || !page.dialog.state) {
    return;
  }

  if (page.dialog.state.type !== 'PlayerSelector') {
    return;
  }

  if (page.dialog.state.data.selection.type === 'new') {
    return;
  }

  if (e.key.length !== 1) {
    return;
  }

  const lKey = e.key.toLowerCase();

  if (!('a' <= lKey && lKey <= 'z')) {
    return;
  }

  store.dispatchCurrentRoomPage({
    type: 'set-dialog',
    data: { ...page.dialog.state,
      data: { ...page.dialog.state.data,
        selection: { type: 'new' },
      },
    },
  });

  const textbox = document.querySelector('#new-option-textbox');

  if (!textbox) {
    return;
  }

  // TODO: type safety?
  (textbox as HTMLInputElement).focus();
});

const frameJobs: ((dt: number) => boolean)[] = [];

function frameLoop() {
  const now = Date.now();
  const dt = now - lastTime;
  let needed = false;

  for (const job of frameJobs) {
    const thisJobNeeded = job(dt);
    needed = needed || thisJobNeeded;
  }

  lastTime = now;

  if (needed) {
    requestAnimationFrame(frameLoop);
  } else {
    frameLoopRunning = false;
  }
}

frameJobs.push((dt) => {
  const app = store.getState();

  if (!App.needsTimeUpdate(app)) {
    return false;
  }

  store.dispatchCurrentRoomPage({ type: 'time-elapsed', data: dt });

  return true;
});

(() => {
  // Simulator

  const normRand = NormalRand(Math.random);

  const players = [
    'Alice',
    'Bob',
    'Carol',
    'Doug',
    'Esther',
    'Frank',
    'Gertrude',
    'Harry',
    'Isabelle',
    'Jerry'
  ].map(name => Player({ name }));

  const hiddenX = players.map(() => 3 * normRand());
  const hiddenXAvg = hiddenX.reduce((a, b) => a + b) / hiddenX.length;
  const hiddenXScaled = hiddenX.map(x => x - hiddenXAvg);

  const hiddenRatings: RefMap<Player, number> = players.reduce(
    (acc, p, i) => RefMap.set(acc, p.ref, hiddenXScaled[i]),
    RefMap<Player, number>(),
  );

  const sim: Simulator = {
    players,
    hiddenRatings,
    queuedGames: 0,
  };

  store.dispatchCurrentRoomPage({ type: 'set-simulator', data: sim });
})();

let syncOff: () => void = () => undefined;
let syncRoom: Room | null = null;

function updateSync(app: App) {
  const room = App.CurrentRoom(app);

  if (room === null || room === syncRoom) {
    return;
  }

  if (syncRoom === null || room.ref.id !== syncRoom.ref.id) {
    syncOff();
    syncRoom = room;

    syncOff = storage.sync(syncRoom.ref.id, syncRoom, isPreferredRoom, (newSyncRoom) => {
      syncRoom = newSyncRoom;

      if (syncRoom === null) {
        throw new Error('Should not be possible');
      }

      if (syncRoom.paused === undefined) {
        syncRoom.paused = {
          players: RefMap<Player, boolean>(),
          playerPairs: [],
        };
      }

      store.dispatch({
        type: 'AddRoom', // TODO: rename -> UpdateRoom?
        data: syncRoom,
      });
    });
  } else {
    syncRoom = room;
    storage.set(syncRoom.ref.id, syncRoom, isPreferredRoom);
  }
}

type RoomHints = { [id: string]: string | null };
let syncEntities = store.getState().entities;

function updateRoomHintsSync(app: App) {
  if (app.entities === syncEntities) {
    return;
  }

  syncEntities = app.entities;
  localStorage.setItem('bb:roomHints', JSON.stringify(App.RoomHints(app)));
}

window.addEventListener('storage', evt => {
  if (evt.key !== 'bb:roomHints') {
    return;
  }

  store.dispatch({
    type: 'SetRoomHints',
    data: JSON.parse(localStorage.getItem('bb:roomHints') || '{}'),
  });
});

render();
store.subscribe(render);

registerServiceWorker();

window.addEventListener('load', () => {
  const cvs = document.querySelector('#qr-canvas');
  qrcode.toCanvas(cvs, location.href, logIfError);
});

function logIfError(...args: any[]) { // tslint:disable-line
  if (args[0]) {
    console.error(args[0]);
  }
}



// WEBPACK FOOTER //
// ./src/index.tsx